import { addMoney, multiplyMoney, percentageOf } from "./money.js";
import type {
  BeneficiaryInput,
  MoneyCents,
  PricingComponentType,
  ProductCode,
  UUID,
} from "./types.js";

export interface PricingComponent {
  id: UUID;
  code: string;
  productCode: ProductCode;
  label: string;
  type: PricingComponentType;
  amountCents?: MoneyCents;
  basisPoints?: number;
  installments?: number;
  baseComponentCode?: string;
  appliesToRoles?: string[];
  selectedByDefault: boolean;
  required: boolean;
  metadata?: Record<string, unknown>;
}

export interface PricingContext {
  beneficiaries: BeneficiaryInput[];
  selectedComponentCodes: string[];
  quantityOverrides?: Record<string, number>;
}

export interface QuoteLine {
  componentId: UUID;
  componentCode: string;
  productCode: ProductCode;
  label: string;
  quantity: number;
  unitAmountCents: MoneyCents;
  totalAmountCents: MoneyCents;
  cadence: "ONE_TIME" | "INSTALLMENT" | "RECURRING" | "DISCOUNT";
  installments: number;
  metadata: Record<string, unknown>;
}

export interface PricingQuote {
  lines: QuoteLine[];
  oneTimeTotalCents: MoneyCents;
  installmentTotalCents: MoneyCents;
  recurringTotalCents: MoneyCents;
  discountTotalCents: MoneyCents;
  grandFirstCycleCents: MoneyCents;
  snapshot: PricingSnapshot;
}

export interface PricingSnapshot {
  engineVersion: "1.0";
  generatedAt: string;
  beneficiaryCount: number;
  billableCount: number;
  selectedComponentCodes: string[];
  lines: Array<{
    componentCode: string;
    type: PricingComponentType;
    quantity: number;
    unitAmountCents: string;
    totalAmountCents: string;
    installments: number;
  }>;
}

function countApplicable(component: PricingComponent, context: PricingContext): number {
  const override = context.quantityOverrides?.[component.code];
  if (override !== undefined) {
    if (!Number.isSafeInteger(override) || override < 0) {
      throw new Error(`Invalid quantity override for ${component.code}.`);
    }
    return override;
  }

  if (!component.appliesToRoles || component.appliesToRoles.length === 0) {
    return context.beneficiaries.filter((beneficiary) => beneficiary.billable).length;
  }

  return context.beneficiaries.filter(
    (beneficiary) =>
      beneficiary.billable &&
      beneficiary.roles.some((role) => component.appliesToRoles?.includes(role)),
  ).length;
}

function isSelected(component: PricingComponent, context: PricingContext): boolean {
  return (
    component.required ||
    component.selectedByDefault ||
    context.selectedComponentCodes.includes(component.code)
  );
}

export function calculateQuote(
  components: readonly PricingComponent[],
  context: PricingContext,
  now = new Date(),
): PricingQuote {
  const selected = components.filter((component) => isSelected(component, context));
  const baseTotals = new Map<string, MoneyCents>();
  const lines: QuoteLine[] = [];

  for (const component of selected) {
    if (
      component.type === "RECURRING_PERCENTAGE_OF_BASE" ||
      component.type === "DISCOUNT_PERCENTAGE"
    ) {
      continue;
    }

    const amount = component.amountCents ?? 0n;
    const perPerson =
      component.type === "ONE_TIME_PER_PERSON" ||
      component.type === "INSTALLMENT_PER_PERSON" ||
      component.type === "RECURRING_PER_PERSON" ||
      component.type === "ADDON_PER_PERSON";
    const quantity = perPerson ? countApplicable(component, context) : 1;
    const rawTotal = multiplyMoney(amount, quantity);

    const cadence: QuoteLine["cadence"] =
      component.type === "INSTALLMENT_PER_PERSON"
        ? "INSTALLMENT"
        : component.type.startsWith("RECURRING")
          ? "RECURRING"
          : component.type.startsWith("DISCOUNT")
            ? "DISCOUNT"
            : "ONE_TIME";

    const signedTotal = cadence === "DISCOUNT" ? -rawTotal : rawTotal;
    const installments = component.installments ?? 1;

    const line: QuoteLine = {
      componentId: component.id,
      componentCode: component.code,
      productCode: component.productCode,
      label: component.label,
      quantity,
      unitAmountCents: amount,
      totalAmountCents: signedTotal,
      cadence,
      installments,
      metadata: component.metadata ?? {},
    };

    lines.push(line);
    baseTotals.set(component.code, signedTotal);
  }

  for (const component of selected) {
    if (
      component.type !== "RECURRING_PERCENTAGE_OF_BASE" &&
      component.type !== "DISCOUNT_PERCENTAGE"
    ) {
      continue;
    }

    if (!component.baseComponentCode) {
      throw new Error(`${component.code} requires baseComponentCode.`);
    }
    const base = baseTotals.get(component.baseComponentCode);
    if (base === undefined) {
      throw new Error(
        `${component.code} references unavailable base ${component.baseComponentCode}.`,
      );
    }

    const amount = percentageOf(base < 0n ? -base : base, component.basisPoints ?? 0);
    const discount = component.type === "DISCOUNT_PERCENTAGE";
    const total = discount ? -amount : amount;

    lines.push({
      componentId: component.id,
      componentCode: component.code,
      productCode: component.productCode,
      label: component.label,
      quantity: 1,
      unitAmountCents: amount,
      totalAmountCents: total,
      cadence: discount ? "DISCOUNT" : "RECURRING",
      installments: 1,
      metadata: component.metadata ?? {},
    });
    baseTotals.set(component.code, total);
  }

  const oneTimeTotalCents = addMoney(
    lines.filter((line) => line.cadence === "ONE_TIME").map((line) => line.totalAmountCents),
  );
  const installmentTotalCents = addMoney(
    lines.filter((line) => line.cadence === "INSTALLMENT").map((line) => line.totalAmountCents),
  );
  const recurringTotalCents = addMoney(
    lines.filter((line) => line.cadence === "RECURRING").map((line) => line.totalAmountCents),
  );
  const discountTotalCents = addMoney(
    lines.filter((line) => line.cadence === "DISCOUNT").map((line) => line.totalAmountCents),
  );

  const firstInstallment = addMoney(
    lines
      .filter((line) => line.cadence === "INSTALLMENT")
      .map((line) => line.totalAmountCents / BigInt(line.installments)),
  );

  const snapshot: PricingSnapshot = {
    engineVersion: "1.0",
    generatedAt: now.toISOString(),
    beneficiaryCount: context.beneficiaries.length,
    billableCount: context.beneficiaries.filter((beneficiary) => beneficiary.billable).length,
    selectedComponentCodes: selected.map((component) => component.code),
    lines: lines.map((line) => ({
      componentCode: line.componentCode,
      type:
        components.find((component) => component.code === line.componentCode)?.type ??
        "ONE_TIME_FIXED",
      quantity: line.quantity,
      unitAmountCents: line.unitAmountCents.toString(),
      totalAmountCents: line.totalAmountCents.toString(),
      installments: line.installments,
    })),
  };

  return {
    lines,
    oneTimeTotalCents,
    installmentTotalCents,
    recurringTotalCents,
    discountTotalCents,
    grandFirstCycleCents:
      oneTimeTotalCents + firstInstallment + recurringTotalCents + discountTotalCents,
    snapshot,
  };
}
